/*
 * Gifting — auto add/remove gift products based on cart subtotal tiers.
 *
 * Configured via theme settings (window.EurusGifting, rendered by
 * snippets/gifting-config.liquid). Each gift line item is tagged with the
 * property `_gift_tier` so it can be identified and (optionally) targeted by a
 * Shopify automatic discount to make it free.
 *
 * The reconcile runs at every moment a gift could need to change:
 *   - on page load / bfcache restore (covers the cart page and any page)
 *   - whenever the theme reports a cart change (eurus:cart:items-changed)
 *   - when the cart drawer opens (or a cart link/icon is clicked)
 *
 * Debug: set `window.EurusGiftingDebug = true` in the browser console (then
 * interact with the cart) to see what it decides in the console.
 */
(function () {
  'use strict';

  var GIFT_PROP = '_gift_tier';
  var processing = false;
  var debounceTimer = null;

  // The theme debounces in-cart quantity edits by 500ms and commits them by
  // LINE INDEX. If we add/remove a gift line during that window we shift the
  // indices and the theme's pending change hits the wrong product. So we never
  // touch the cart until the user has been idle for longer than that debounce
  // (plus the request round-trip) and no theme update is in flight.
  var COOLDOWN = 750;
  var lastInteraction = -Infinity; // no cool-down until the user actually interacts

  function nowMs() {
    return (window.performance && performance.now)
      ? performance.now()
      : Date.now();
  }

  function config() {
    return window.EurusGifting;
  }

  function dbg() {
    if (!window.EurusGiftingDebug) return;
    var args = Array.prototype.slice.call(arguments);
    args.unshift('[gifting]');
    try { console.log.apply(console, args); } catch (e) {}
  }

  function root() {
    return (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  }

  // Tiers that are actually usable, sorted ascending by threshold.
  function activeTiers() {
    var c = config();
    if (!c || !c.enabled || !Array.isArray(c.tiers)) return [];
    return c.tiers
      .filter(function (t) {
        var ok = t && t.threshold > 0 && t.variantId && t.available;
        if (!ok && t) {
          dbg('tier ' + t.tier + ' skipped', {
            threshold: t.threshold, variantId: t.variantId, available: t.available
          });
        }
        return ok;
      })
      .sort(function (a, b) {
        return a.threshold - b.threshold;
      });
  }

  function isGiftItem(item) {
    return !!(item && item.properties && item.properties[GIFT_PROP]);
  }

  function getCart() {
    return fetch(root() + 'cart.js', {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin'
    }).then(function (r) {
      return r.json();
    });
  }

  // Subtotal of paid (non-gift) line items, in cents.
  function qualifyingSubtotal(cart) {
    return (cart.items || []).reduce(function (sum, item) {
      return isGiftItem(item) ? sum : sum + item.final_line_price;
    }, 0);
  }

  function store(name) {
    if (window.Alpine && typeof Alpine.store === 'function') {
      return Alpine.store(name);
    }
    return null;
  }

  function waitForCartUpdate() {
    var helper = store('xCartHelper');
    if (helper && typeof helper.waitForCartUpdate === 'function') {
      return helper.waitForCartUpdate();
    }
    return Promise.resolve();
  }

  // Section ids the theme re-renders for the cart (items, footer, upsell, icon
  // bubbles). We ask the cart mutation endpoints to render these so we can repaint
  // straight from the response.
  function sectionIds() {
    var helper = store('xCartHelper');
    if (helper && typeof helper.getSectionsToRender === 'function') {
      try {
        return helper.getSectionsToRender().map(function (s) { return s.id; });
      } catch (e) {}
    }
    return null;
  }

  // Refresh the cart UI (drawer, footer, cart page, free-shipping bar, bubbles).
  // Prefer repainting from `sections` returned by the mutation itself: it reflects
  // the post-change cart exactly. A separate GET (xMiniCart.reLoad) can be served
  // stale from cache and does not reliably repaint the cart page — that left the
  // old gift tier on screen after the cart had already changed server-side.
  function refresh(changed, sections) {
    var helper = store('xCartHelper');
    if (sections && helper && typeof helper.reRenderSections === 'function') {
      try { helper.reRenderSections(sections); return; } catch (e) {}
    }
    if (helper && typeof helper.reRenderSections === 'function') {
      try { helper.reRenderSections(); return; } catch (e) {}
    }
    var mini = store('xMiniCart');
    if (mini && typeof mini.reLoad === 'function') {
      try { mini.reLoad(); return; } catch (e) {}
    }
    // No Alpine available: only a full reload can reflect the change.
    if (changed) { window.location.reload(); }
  }

  function postJSON(url, body) {
    return fetch(root() + url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(body)
    }).then(function (r) {
      return r.json().catch(function () { return {}; });
    });
  }

  function reconcile() {
    var c = config();
    if (!c || !c.enabled) return;

    var tiers = activeTiers();
    if (!tiers.length) { dbg('no active tiers configured'); return; }

    // Wait until the cart is idle: no reconcile/theme update in flight, and the
    // user hasn't touched a cart control within the cool-down. This guarantees
    // any debounced, line-index-based theme quantity edit has already committed
    // before we shift line indices by adding/removing a gift.
    var sinceInteraction = nowMs() - lastInteraction;
    if (processing || window.updatingCart || sinceInteraction < COOLDOWN) {
      var retry = Math.max(COOLDOWN - sinceInteraction, 120);
      dbg('deferring', {
        processing: processing,
        updatingCart: window.updatingCart,
        sinceInteraction: Math.round(sinceInteraction),
        retryIn: Math.round(retry)
      });
      scheduleReconcile(retry);
      return;
    }

    processing = true;

    getCart()
      .then(function (cart) {
        var subtotal = qualifyingSubtotal(cart);
        var gifts = (cart.items || []).filter(isGiftItem);

        var reached = tiers.filter(function (t) { return subtotal >= t.threshold; });

        var desired; // tiers whose gift should be in the cart
        if (c.unique) {
          desired = reached.length ? [reached[reached.length - 1]] : [];
        } else {
          desired = reached;
        }

        dbg('reconcile', {
          subtotalCents: subtotal,
          unique: !!c.unique,
          reachedTiers: reached.map(function (t) { return t.tier; }),
          desiredTiers: desired.map(function (t) { return t.tier; }),
          giftsInCart: gifts.map(function (g) { return g.properties[GIFT_PROP]; })
        });

        var desiredVariantByTier = {};
        desired.forEach(function (t) {
          desiredVariantByTier[String(t.tier)] = t.variantId;
        });

        // Removals / normalisations, keyed by line-item key -> quantity.
        var updates = {};
        gifts.forEach(function (item) {
          var tierId = String(item.properties[GIFT_PROP]);
          var wantVariant = desiredVariantByTier[tierId];
          if (!wantVariant || item.variant_id !== wantVariant) {
            updates[item.key] = 0; // not wanted (or wrong variant) -> remove
          } else if (item.quantity !== 1) {
            updates[item.key] = 1; // keep exactly one
          }
        });

        // Additions for desired tiers that aren't already present & kept.
        var toAdd = [];
        desired.forEach(function (t) {
          var alreadyKept = gifts.some(function (item) {
            return (
              String(item.properties[GIFT_PROP]) === String(t.tier) &&
              item.variant_id === t.variantId &&
              updates[item.key] !== 0
            );
          });
          if (!alreadyKept) toAdd.push(t);
        });

        if (!Object.keys(updates).length && !toAdd.length) {
          dbg('nothing to change');
          return null;
        }

        dbg('applying', { remove: Object.keys(updates), add: toAdd.map(function (t) { return t.tier; }) });
        return applyChanges(updates, toAdd);
      })
      .catch(function (err) { dbg('error', err); })
      .then(function () { processing = false; });
  }

  function applyChanges(updates, toAdd) {
    return waitForCartUpdate().then(function () {
      window.updatingCart = true;

      // Ask each mutation to render the cart sections so we can repaint from the
      // response (fresh + matches the post-change cart). The LAST mutation in the
      // chain carries the final state, so we repaint from that one.
      var ids = sectionIds();
      var secBody = ids ? { sections: ids, sections_url: window.location.pathname } : null;
      var lastSections = null;

      var chain = Promise.resolve();

      if (Object.keys(updates).length) {
        chain = chain.then(function () {
          var body = { updates: updates };
          if (secBody) { body.sections = secBody.sections; body.sections_url = secBody.sections_url; }
          return postJSON('cart/update.js', body).then(function (res) {
            if (res && res.sections) lastSections = res.sections;
            return res;
          });
        });
      }

      if (toAdd.length) {
        chain = chain.then(function () {
          var items = toAdd.map(function (t) {
            var props = {};
            props[GIFT_PROP] = String(t.tier);
            return { id: t.variantId, quantity: 1, properties: props };
          });
          var body = { items: items };
          if (secBody) { body.sections = secBody.sections; body.sections_url = secBody.sections_url; }
          return postJSON('cart/add.js', body).then(function (res) {
            if (res && res.status) dbg('add.js responded', res.status, res.description || res.message);
            if (res && res.sections) lastSections = res.sections;
            return res;
          });
        });
      }

      return chain
        .catch(function (err) { dbg('apply error', err); })
        .then(function () {
          window.updatingCart = false;
          refresh(true, lastSections);
        });
    });
  }

  function scheduleReconcile(delay) {
    if (debounceTimer) clearTimeout(debounceTimer);
    debounceTimer = setTimeout(reconcile, delay || 150);
  }

  // Watch the mini-cart open state via Alpine so opening the drawer triggers a
  // reconcile. Returns true if the watcher was installed.
  function watchCartOpen() {
    if (!(window.Alpine && typeof Alpine.effect === 'function')) return false;
    try {
      Alpine.effect(function () {
        var mini = Alpine.store('xMiniCart');
        // Touch .open so the effect re-runs whenever it changes.
        if (mini && mini.open) {
          dbg('cart drawer opened');
          scheduleReconcile();
        }
      });
      return true;
    } catch (e) {
      return false;
    }
  }

  // Watch the cart UI for re-renders. The theme re-renders the cart bubble /
  // drawer / cart-items by replacing their innerHTML on EVERY cart mutation
  // (including in-cart quantity +/- and line removals, which do NOT dispatch
  // eurus:cart:items-changed). An innerHTML replacement is a childList mutation,
  // so this catches them all. `childList` only (no subtree / characterData)
  // avoids firing on nested countdown-timer text ticks.
  function observeCart() {
    if (typeof MutationObserver === 'undefined') return;
    var selectors = [
      '#cart-icon-bubble',
      '#mobile-cart-icon-bubble',
      '#cart-icon-bubble-mobile-dock',
      '#CartDrawer',
      '#main-cart-items',
      '#main-cart-footer'
    ];
    var targets = [];
    selectors.forEach(function (sel) {
      var el = document.querySelector(sel);
      if (el) targets.push(el);
    });
    if (!targets.length) return;
    var obs = new MutationObserver(function () {
      dbg('cart DOM changed');
      scheduleReconcile();
    });
    targets.forEach(function (el) {
      obs.observe(el, { childList: true });
    });
    dbg('observing cart targets', targets.length);
  }

  // Poll until the Alpine cart store exists, then run cb (and set up the
  // open-watcher). Falls back after a timeout so the reconcile still runs.
  function whenReady(cb) {
    if (store('xCartHelper')) { cb(); return; }
    var tries = 0;
    var iv = setInterval(function () {
      if (store('xCartHelper') || tries++ > 50) {
        clearInterval(iv);
        cb();
      }
    }, 100);
  }

  function init() {
    var c = config();
    if (!c || !c.enabled) { return; }
    dbg('init', c);

    // 1. Any cart mutation the theme performs.
    document.addEventListener('eurus:cart:items-changed', function () {
      scheduleReconcile();
    });

    // 2. Track user activity inside the cart (quantity +/-, typing a quantity,
    //    removing a line). While the user is active — and for a cool-down after
    //    — we hold off touching the cart so the theme's debounced, line-index
    //    based edits commit against stable indices.
    ['pointerdown', 'click', 'change', 'input', 'keydown'].forEach(function (evt) {
      document.addEventListener(evt, function (e) {
        if (e.target && e.target.closest &&
            e.target.closest('#CartDrawer, #main-cart-items, #main-cart-footer')) {
          lastInteraction = nowMs();
        }
      }, true);
    });

    // 3. Clicking a cart icon / link (backup for the drawer-open watcher).
    document.addEventListener('click', function (e) {
      if (!e.target || !e.target.closest) return;
      var hit = e.target.closest(
        '#cart-icon-bubble, #mobile-cart-icon-bubble, #cart-icon-bubble-mobile-dock,' +
        ' a[href$="/cart"], [data-cart-toggle]'
      );
      if (hit) scheduleReconcile();
    }, true);

    // 4. Cart UI re-renders — catches in-cart quantity changes & removals that
    //    the theme does NOT announce via eurus:cart:items-changed.
    observeCart();

    // 5. bfcache restore (back/forward navigation).
    window.addEventListener('pageshow', function () { scheduleReconcile(); });

    // 6. Drawer-open watcher + initial pass, once Alpine is ready.
    whenReady(function () {
      watchCartOpen();
      scheduleReconcile();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
